import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { getRentalColumns } from './rentals/RentalTableColumns';
import { RentalActions } from './rentals/RentalActions';
import { ConnectionErrorAlert } from './rentals/ConnectionErrorAlert';
import { RentalDialogs } from './rentals/components/RentalDialogs';
import { BikeDetailsDialog } from './rentals/components/BikeDetailsDialog';
import { RentalStatsCard } from './rentals/components/RentalStatsCard';
import { useRentalsData } from './rentals/hooks/useRentalsData';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { deleteRental } from './rentals/services/rentalMutationApi';
import { cancelSubscriptionsForRental } from '@/hooks/api/rentalService';

const RentalsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rentalIdFilter = searchParams.get('id');
  const action = searchParams.get('action');

  const {
    rentals,
    allRentals,
    bikes,
    couriers,
    loading,
    statsLoading,
    selectedRental,
    showAddDialog,
    showDetailsDialog,
    connectionError,
    dataSource,
    isEditing,
    isFormOpen,
    totalRevenue,
    totalCount,
    setSelectedRental,
    setShowAddDialog,
    setShowDetailsDialog,
    setStatusFilter,
    setIsFormOpen,
    fetchData,
    handleAddRental,
    handleChangeStatus,
    handleEditRental,
    handleRefreshData
  } = useRentalsData();

  const [viewBikeId, setViewBikeId] = useState<string | null>(null);
  const [showBikeDetails, setShowBikeDetails] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);

  // Если есть параметр id в URL, найдем аренду и покажем детали
  useEffect(() => {
    if (rentalIdFilter && rentals.length > 0) {
      const rental = rentals.find(r => r.id === rentalIdFilter);
      if (rental) {
        setSelectedRental(rental);
        setShowDetailsDialog(true);
      }
    }
    
    // Если есть параметр action=create, показываем диалог создания аренды
    if (action === 'create') {
      setShowAddDialog(true);
      // Очищаем URL после обработки действия
      navigate('/admin/rentals', { replace: true });
    }
  }, [rentalIdFilter, rentals, action, setSelectedRental, setShowDetailsDialog, setShowAddDialog, navigate]);

  // Обработчик обновления данных с флагом для предотвращения мерцания
  useEffect(() => {
    // Не обновляем данные, если открыта форма редактирования
    if (shouldRefresh && !isFormOpen && !isEditing) {
      handleRefreshData();
      setShouldRefresh(false);
    }
  }, [shouldRefresh, handleRefreshData, isFormOpen, isEditing]);

  // Фильтруем аренды, если есть параметр id
  const displayedRentals = rentalIdFilter 
    ? rentals.filter(rental => rental.id === rentalIdFilter)
    : rentals;

  // Сбрасываем фильтр и закрываем диалог
  const handleClearFilter = () => {
    // Перенаправляем на ту же страницу, но без параметра id
    navigate('/admin/rentals');
  };

  // Закрываем диалог с деталями и сбрасываем фильтр
  const handleCloseDetailsDialog = () => {
    setShowDetailsDialog(false);
    if (rentalIdFilter) {
      handleClearFilter();
    }
  };

  // Обработчик удаления аренды с улучшенным логированием
  const handleDeleteRentalConfirm = async () => {
    if (!selectedRental) return;
    
    setIsDeleting(true);
    try {
      console.log('Начинаем процесс удаления аренды:', selectedRental.id);
      
      // Сначала отменяем связанные подписки
      console.log('Отмена связанных подписок...');
      const cancelResult = await cancelSubscriptionsForRental(selectedRental.id);
      
      if (cancelResult.success && cancelResult.message.includes('Отменено')) {
        toast.info(`Связанные подписки: ${cancelResult.message}`);
      }
      
      // Теперь удаляем аренду с каскадным удалением
      console.log('Каскадное удаление аренды...');
      const result = await deleteRental(selectedRental.id);
      
      if (!result.success) {
        throw new Error(`Ошибка при удалении: ${result.error?.message || JSON.stringify(result.error)}`);
      }
      
      // Уведомляем пользователя о результате
      toast.success(`Аренда ${selectedRental.human_number || selectedRental.id.substring(0, 8)} и все связанные данные удалены`);
      
      // Сначала закрываем диалоги
      setShowDeleteDialog(false);
      setShowDetailsDialog(false);
      
      if (rentalIdFilter) {
        handleClearFilter();
      }
      
      // Устанавливаем флаг для обновления данных после закрытия диалога
      setTimeout(() => {
        setShouldRefresh(true);
      }, 300);
      
    } catch (error: any) {
      console.error('Ошибка при удалении аренды:', error);
      toast.error(`Не удалось удалить аренду: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewBike = (bikeId: string) => {
    setViewBikeId(bikeId);
    setShowBikeDetails(true);
  };

  const handleEditRentalClick = (rental: any) => {
    setSelectedRental(rental);
    setShowEditDialog(true);
  };

  const handleDeleteRentalClick = (rental: any) => {
    setSelectedRental(rental);
    setShowDeleteDialog(true);
  };

  // Обработчик обновления данных аренды с предотвращением мерцания
  const handleRentalUpdate = () => {
    // Если форма редактирования не открыта, обновляем данные
    if (!isFormOpen && !isEditing) {
      setShouldRefresh(true);
    }
  };

  const columns = getRentalColumns(
    handleViewBike, 
    handleEditRentalClick,
    handleDeleteRentalClick
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Управление арендами</h1>
        <RentalActions 
          onAddClick={() => setShowAddDialog(true)}
          onStatusFilterChange={setStatusFilter}
          onRefresh={() => {
            if (!isFormOpen && !isEditing) {
              setShouldRefresh(true);
            } else {
              toast.info("Обновление данных отложено, т.к. открыта форма редактирования");
            }
          }}
          dataSource={dataSource}
        />
      </div>

      <ConnectionErrorAlert 
        connectionError={connectionError} 
        dataSource={dataSource}
        onRetry={() => {
          if (!isFormOpen && !isEditing) {
            setShouldRefresh(true);
          } else {
            toast.info("Обновление данных отложено, т.к. открыта форма редактирования");
          }
        }}
      />

      {/* Передаем выручку из платежей в RentalStatsCard */}
      <RentalStatsCard 
        rentals={allRentals} 
        totalCount={totalCount}
        totalRevenue={totalRevenue} 
        loading={statsLoading}
      />

      {rentalIdFilter && (
        <div className="bg-muted/30 border rounded-md p-3 flex items-center justify-between">
          <div>
            <span className="text-sm font-medium">Фильтр по аренде: </span>
            <span className="text-sm text-muted-foreground">
              {rentals.find(r => r.id === rentalIdFilter)?.human_number || rentalIdFilter}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearFilter}
          >
            <X className="h-4 w-4 mr-1" />
            Сбросить фильтр
          </Button>
        </div>
      )}

      <DataTable
        data={displayedRentals}
        columns={columns}
        searchable
        searchPlaceholder="Поиск аренд..."
        loading={loading && !isFormOpen}
        onRowClick={(rental) => {
          setSelectedRental(rental);
          setShowDetailsDialog(true);
        }}
      />

      <RentalDialogs
        selectedRental={selectedRental}
        showAddDialog={showAddDialog}
        showDetailsDialog={showDetailsDialog}
        showEditDialog={showEditDialog}
        showDeleteDialog={showDeleteDialog}
        setShowAddDialog={setShowAddDialog}
        setShowDetailsDialog={handleCloseDetailsDialog}
        setShowEditDialog={setShowEditDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        handleAddRental={handleAddRental}
        handleEditRental={handleEditRental}
        handleDeleteRental={handleDeleteRentalConfirm}
        handleChangeStatus={handleChangeStatus}
        handleRentalUpdate={handleRentalUpdate}
        setIsFormOpen={setIsFormOpen}
        loading={loading || isDeleting}
        bikes={bikes}
        couriers={couriers}
      />

      {/* Диалоговое окно для просмотра информации о велосипеде из таблицы */}
      <BikeDetailsDialog
        bikeId={viewBikeId}
        open={showBikeDetails}
        onOpenChange={setShowBikeDetails}
      />
    </div>
  );
};

export default RentalsPage;
